import { ErrorBoundary } from "@sentry/react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, X } from "lucide-react";
import { useEffect } from "react";
import { useConnectDispatch, useConnectState } from "./ConnectContext";
import UnexpectedErrorFallback from "./components/UnexpectedError";
import { StepDots } from "./components/StepDots";
import { sendExit } from "./messages";
import InitStep from "./steps/0_Init";
import InstitutionSelectStep from "./steps/1_InstitutionSelect";
import PermissionsStep from "./steps/2_Permissions";
import AuthenticationStep from "./steps/3_Authentication";
import AccountSelectStep from "./steps/4_AccountSelect";
import FinishedStep from "./steps/5_Finished";
import { ActionType, Steps } from "./types";
import { BankThemeProvider, useBankTheme } from "@/lib/bank-theme";

// Step transition variants
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
    scale: 0.98,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -20 : 20,
    opacity: 0,
    scale: 0.98,
  }),
};

const stepTransition = {
  type: "spring",
  stiffness: 400,
  damping: 35,
  mass: 0.8,
};

// Inner component that uses the bank theme context
const ConnectInner = () => {
  const { currentStep, isReconnect, institution } = useConnectState();
  const dispatch = useConnectDispatch();
  const { setTheme } = useBankTheme();

  // Update bank theme when institution changes or reset when returning to Init
  useEffect(() => {
    if (currentStep === Steps.Init) {
      setTheme(null);
    } else if (institution) {
      setTheme(institution);
    }
  }, [currentStep, institution, setTheme]);

  // Track direction for animations
  const direction = 1; // Forward by default

  const renderStep = () => {
    switch (currentStep) {
      case Steps.Init:
        return <InitStep />;
      case Steps.InstitutionSelect:
        return <InstitutionSelectStep />;
      case Steps.Permissions:
        return <PermissionsStep />;
      case Steps.Authentication:
        return <AuthenticationStep />;
      case Steps.AccountSelect:
        return <AccountSelectStep />;
      case Steps.Finished:
        return <FinishedStep />;
      default:
        return <h1>Step {currentStep} [Not implemented]</h1>;
    }
  };

  const handleBack = () => {
    dispatch({ type: ActionType.Back });
  };

  const handleClose = () => {
    sendExit();
    setTimeout(() => {
      dispatch({ type: ActionType.Reset });
    }, 0);
  };

  const NO_BACK_STEPS = [Steps.Init, Steps.AccountSelect, Steps.Finished];

  // Total steps for progress (excluding Init which is step 0)
  const totalSteps = 5;
  const currentStepIndex = currentStep;

  return (
    <div className="flex flex-col items-center justify-center h-full w-full fixed select-none bg-black/60 backdrop-blur-[2px]">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
        className="md:w-[380px] md:h-[640px] h-full w-full bg-background shadow-2xl flex flex-col md:rounded-2xl overflow-hidden border border-border/50"
      >
        <ErrorBoundary fallback={UnexpectedErrorFallback}>
          {/* Header */}
          <div className="flex-shrink-0 flex flex-row items-center px-2 py-2 border-b border-border/50">
            {/* Back button */}
            <div className="flex-1">
              {!isReconnect && !NO_BACK_STEPS.includes(currentStep) && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 active:scale-95"
                  onClick={handleBack}
                  data-ph-capture-attribute-action="back"
                >
                  <ChevronLeft className="w-5 h-5 text-muted-foreground" />
                </motion.button>
              )}
            </div>

            {/* Progress indicator */}
            <div className="flex-1 flex justify-center">
              {!isReconnect && currentStep > Steps.Init && (
                <StepDots
                  totalSteps={totalSteps}
                  currentStep={currentStepIndex}
                />
              )}
            </div>

            {/* Close button */}
            <div className="flex-1 flex justify-end">
              <button
                className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 active:scale-95"
                onClick={handleClose}
                data-ph-capture-attribute-action="close"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={stepTransition}
              className="flex-1 min-h-0 flex flex-col overflow-hidden"
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </ErrorBoundary>
      </motion.div>
    </div>
  );
};

// Wrapper component with provider
const Connect = () => {
  return (
    <BankThemeProvider>
      <ConnectInner />
    </BankThemeProvider>
  );
};

export default Connect;
